import { eveSupabase } from '@/config/eveBackend';

/**
 * Feature flags controlled by environment variables.
 * Set VITE_ENABLE_<FLAG>=true to enable features.
 * 
 * For production, these should be set to 'false' or omitted.
 * For staging/development, set them to 'true'.
 */

export const featureFlags = {
  // Admin page tabs
  showAdminLenders: import.meta.env.VITE_ENABLE_ADMIN_LENDERS === 'true',
  showAdminEntities: import.meta.env.VITE_ENABLE_ADMIN_ENTITIES === 'true',
  showAdminAlerts: import.meta.env.VITE_ENABLE_ADMIN_ALERTS === 'true',

  // Organisation Profile tabs
  showOrgBilling: import.meta.env.VITE_ENABLE_ORG_BILLING === 'true',

  // Product Mappings
  showProductMappings: import.meta.env.VITE_ENABLE_PRODUCT_MAPPINGS === 'true',

  // Inventory sub-tabs
  showInventoryTrends: import.meta.env.VITE_ENABLE_INVENTORY_TRENDS === 'true',
  showInventoryFinancingTapes: import.meta.env.VITE_ENABLE_INVENTORY_FINANCING === 'true',
  showInventoryWhatIf: import.meta.env.VITE_ENABLE_INVENTORY_WHATIF === 'true',
  showInventoryAdjust: import.meta.env.VITE_ENABLE_INVENTORY_ADJUST === 'true',
  showVariantMapping: import.meta.env.VITE_ENABLE_VARIANT_MAPPING === 'true',

  // Reports tab features
  showReportsNervProjections: import.meta.env.VITE_ENABLE_REPORTS_NERV_PROJECTIONS === 'true',

  // Admin Connections tab
  showAdminConnections: import.meta.env.VITE_ENABLE_ADMIN_CONNECTIONS === 'true',
};

/** The only hostname that serves the production app. */
const PRODUCTION_HOSTNAME = 'onboarding.figabl.com';

/** The only hostname that serves the shared staging app. */
const STAGING_HOSTNAME = 'staging-onboarding.figabl.com';

export function isProductionUrl(): boolean {
  return typeof window !== 'undefined' && window.location.hostname === PRODUCTION_HOSTNAME;
}

export function isStagingUrl(): boolean {
  return typeof window !== 'undefined' && window.location.hostname === STAGING_HOSTNAME;
}

/**
 * Migrate to Staging (admin) is on for production and the shared staging host.
 * Production is the real copy-from-live path; staging is enabled so operators
 * can reach the same admin entry from https://staging-onboarding.figabl.com/admin.
 * Vercel previews, local dev, and any later host stay off by default.
 *
 * VITE_ENABLE_MIGRATE_TO_STAGING overrides it either way — 'false' kills the
 * feature without a code change, 'true' turns it on elsewhere (e.g. a preview
 * branch) for deliberate testing.
 */
export function isMigrateToStagingEnabled(): boolean {
  const override = import.meta.env.VITE_ENABLE_MIGRATE_TO_STAGING;
  if (override === 'true') return true;
  if (override === 'false') return false;
  return isProductionUrl() || isStagingUrl();
}

export function isLocalSupabaseUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return host === '127.0.0.1' || host === 'localhost' || host === '::1';
  } catch {
    return false;
  }
}

/**
 * Custom SMS MFA is required against hosted Supabase (staging/prod).
 * Local `figa` / `127.0.0.1` skips it so login works without Twilio OTP.
 * `VITE_ENABLE_MFA=true` forces it on, including locally.
 * `VITE_ENABLE_MFA=false` is ignored for hosted backends.
 */
export function resolveMfaRequired(options: {
  enableMfa?: string;
  supabaseUrl?: string;
}): boolean {
  if (options.enableMfa === 'true') return true;
  if (!isLocalSupabaseUrl(options.supabaseUrl)) {
    if (options.enableMfa === 'false') {
      console.warn('VITE_ENABLE_MFA=false is ignored for a hosted Supabase URL');
    }
    return true;
  }
  return false;
}

export function isMfaRequired(): boolean {
  return resolveMfaRequired({
    enableMfa: import.meta.env.VITE_ENABLE_MFA,
    supabaseUrl: eveSupabase.url,
  });
}
